import { useState, useEffect } from "react";
import { btnPrimary, inputStyle, labelStyle, Spinner } from "./shared";

// Public, no-login lease signing page. Reached via a tokenized link emailed
// by create-lease (e.g. https://leaslypro.com/sign-lease?token=...). Auth is
// entirely via the token in the URL — there is no Supabase session here, since
// a tenant may not have a portal account at all yet at lease time.
export default function LeaseSign({ supabase }) {
  const token = new URLSearchParams(window.location.search).get("token");

  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [lease, setLease]     = useState(null);
  const [company, setCompany] = useState(null);

  const [typedName, setTypedName] = useState("");
  const [agreed, setAgreed]       = useState(false);
  const [signing, setSigning]     = useState(false);
  const [signError, setSignError] = useState("");
  const [justSigned, setJustSigned] = useState(false);

  useEffect(() => {
    if (!token) { setError("This link is missing its signing token."); setLoading(false); return; }
    (async () => {
      const { data, error: fnErr } = await supabase.functions.invoke("sign-lease", {
        body: { action: "load", token },
      });
      setLoading(false);
      if (fnErr || data?.error) {
        setError(data?.error || fnErr?.message || "Could not load this lease.");
        return;
      }
      setLease(data.lease);
      setCompany(data.company);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSign = async () => {
    setSignError("");
    if (!typedName.trim()) { setSignError("Type your full legal name to sign."); return; }
    if (!agreed) { setSignError("You must check the box to consent to sign electronically."); return; }

    setSigning(true);
    const { data, error: fnErr } = await supabase.functions.invoke("sign-lease", {
      body: { action: "sign", token, typedName: typedName.trim(), agreed: true },
    });
    setSigning(false);
    if (fnErr || data?.error) {
      setSignError(data?.error || fnErr?.message || "Could not record your signature.");
      return;
    }
    setJustSigned(true);
  };

  const wrap = { minHeight: "100vh", background: "#F5F1EA", display: "flex", justifyContent: "center", padding: "40px 16px", fontFamily: "'Manrope', sans-serif" };
  const card = { background: "#FFFFFF", border: "1px solid #e0dbd0", borderRadius: 16, padding: "32px 28px", width: 680, maxWidth: "100%", height: "fit-content" };

  if (loading) {
    return (
      <div style={{ ...wrap, alignItems: "center" }}>
        <div style={{ textAlign: "center" }}>
          <Spinner />
          <div style={{ color: "#9B9690", marginTop: 12 }}>Loading your lease…</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...wrap, alignItems: "center" }}>
        <div style={{ ...card, textAlign: "center", width: 440 }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 20, color: "#163C2F", marginBottom: 8 }}>Can't open this lease</div>
          <div style={{ color: "#9B9690", fontSize: 14 }}>{error}</div>
        </div>
      </div>
    );
  }

  const signedConfirmation = justSigned || lease.alreadySigned;

  return (
    <div style={wrap}>
      <div style={card}>
        <div style={{ textAlign: "center", paddingBottom: 16, borderBottom: "2px solid #163C2F", marginBottom: 20 }}>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 18, color: "#163C2F", fontVariant: "small-caps", letterSpacing: "0.08em" }}>
            {company?.company_name || "Leasly"}
          </div>
          <div style={{ fontSize: 12, color: "#9B9690", marginTop: 4 }}>Residential Lease Agreement — {lease.propertyAddress || "Your Unit"}</div>
        </div>

        {signedConfirmation ? (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 20, color: "#163C2F", marginBottom: 8 }}>
              This lease has been signed
            </div>
            <div style={{ color: "#9B9690", fontSize: 13, lineHeight: 1.6 }}>
              Signed by {lease.signerTypedName || typedName}
              {lease.signedAt ? ` on ${new Date(lease.signedAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}` : ""}.
              A copy has been sent to your landlord.
            </div>
          </div>
        ) : (
          <>
            <pre style={{
              background: "#F5F1EA", border: "1px solid #e0dbd0", borderRadius: 10,
              padding: "16px 18px", fontSize: 12, color: "#2d4a2d",
              whiteSpace: "pre-wrap", fontFamily: "Georgia, 'Times New Roman', serif",
              lineHeight: 1.75, maxHeight: 420, overflowY: "auto", marginBottom: 24,
            }}>{lease.documentText}</pre>

            <div style={{ background: "#F9F7F4", border: "1px solid #e0dbd0", borderRadius: 12, padding: "20px 22px" }}>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 16, color: "#163C2F", marginBottom: 14 }}>Sign this lease</div>

              <label style={labelStyle}>Type your full legal name</label>
              <input
                type="text" value={typedName} onChange={e => setTypedName(e.target.value)}
                placeholder={lease.tenantName || "Your full name"} style={{ ...inputStyle, marginBottom: 14 }}
              />

              <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", marginBottom: 16 }}>
                <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
                  style={{ marginTop: 3, width: 16, height: 16, accentColor: "#163C2F", cursor: "pointer", flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: "#2B2B2B", lineHeight: 1.6 }}>
                  I agree that typing my name above constitutes my legal electronic signature, and I consent to
                  sign and conduct this transaction electronically.
                </span>
              </label>

              {signError && (
                <div style={{ background: "#FEF2F2", border: "1px solid #fca5a5", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#dc2626", marginBottom: 14 }}>
                  {signError}
                </div>
              )}

              <button onClick={handleSign} disabled={signing} style={{ ...btnPrimary, width: "100%", opacity: signing ? 0.6 : 1 }}>
                {signing ? "Signing…" : "✍️ Sign Lease"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
